/**
 * Placement des panneaux ouverts depuis la barre d'outils : à droite de la
 * BARRE, pas du bouton. En deux colonnes, un panneau ouvert depuis la 1re
 * colonne passe ainsi par-dessus la 2e au lieu de la masquer.
 */

import { zoomedReferenceModifier } from "@/utils/zoomedPopperReference";

const TOOLBAR_SELECTOR = "[data-canvas-toolbar]";

interface PopperRect {
  width: number;
}

/**
 * Distance entre le bord droit d'un outil et celui de la barre, dans le
 * repère de Popper (`referenceWidth` : largeur de l'outil vue par Popper).
 * Les panneaux Bootstrap vivent dans la barre zoomée, les popovers Element
 * Plus dans `body` : le rapport des largeurs couvre les deux cas.
 */
export function distanceToToolbarEdge(
  tool: Element,
  referenceWidth: number,
): number {
  const toolbar = tool.closest(TOOLBAR_SELECTOR);
  if (!toolbar) return 0;
  const toolRect = tool.getBoundingClientRect();
  const edge = toolbar.getBoundingClientRect().right;
  const ratio = toolRect.width > 0 ? referenceWidth / toolRect.width : 1;
  return Math.max(0, (edge - toolRect.right) * ratio);
}

/** Écart entre la barre et un panneau d'options Bootstrap. */
const DROPDOWN_GAP = 8;

/**
 * Option `offset` des dropdowns Bootstrap des outils (cf. ToolOptionsDropdown).
 * Bootstrap l'appelle avec les rectangles de Popper et le déclencheur.
 */
export function toolbarDropdownOffset(
  popperData?: { reference: PopperRect },
  toggler?: HTMLElement,
): [number, number] {
  if (!popperData || !toggler) return [0, DROPDOWN_GAP];
  const distance = distanceToToolbarEdge(toggler, popperData.reference.width);
  return [0, distance + DROPDOWN_GAP];
}

/** `popper-options` d'un popover Element Plus ancré à un outil de la barre. */
export function toolbarPopperOptions(
  getTool: () => Element | null | undefined,
  gap: number,
) {
  return {
    modifiers: [
      // Barre zoomée, popover dans <body> : recalage sur Safari et les anciens
      // Chromium (cf. zoomedPopperReference).
      zoomedReferenceModifier,
      {
        name: "offset",
        options: {
          offset: ({ reference }: { reference: PopperRect }) => {
            const tool = getTool();
            const distance = tool
              ? distanceToToolbarEdge(tool, reference.width)
              : 0;
            return [0, distance + gap];
          },
        },
      },
    ],
  };
}
